














































import { Component, Vue as VueDecorator } from "vue-property-decorator";
import { auth } from "firebase/app";
import {
  SharedEmailAddressInfo,
  SharedEmailAddressUserInfo,
} from "@/data/models/SharedEmailAddressInfo";
import {
  getActiveUser,
  setActiveUser,
  signInWithUserOfSharedEmailAddress,
} from "@/data/repository/UserRepository";
import { LoginResult } from "./LoginTypes";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { signInRefusalMessageKey, toSignInRefusalReason } from "@/utils/SignInRefusal";
import { clearUserModule } from "@/utils/Utils";
import { COLOR_BUTTON_GREEN, COLOR_BUTTON } from "@/utils/colors";
import { themedFill, themedFillTextClass } from "@/utils/Theme";

@Component
export default class SharedEmailAddresOptionsDialogComponent extends VueDecorator {
  isShowing = false;
  isLoading = false;

  resolve?: (value: LoginResult) => void = undefined;
  reject?: (reason: string) => void = undefined;

  sharedEmailAddressInfo: SharedEmailAddressInfo | null = null;
  sharedUserPassword: string | null = null;
  users: SharedEmailAddressUserInfo[] = [];

  calledViaHeader = false;

  /** The option's fill for the active theme: the palette green and the accent red in dark mode. */
  getOptionFill(sharedEmailAddressUserInfo: SharedEmailAddressUserInfo) {
    const color = this.getOptionColor(sharedEmailAddressUserInfo);

    return color ? themedFill(color) : color;
  }

  /** The option's label class: white in light mode; dark on the palette green in dark mode. */
  getOptionTextClass(sharedEmailAddressUserInfo: SharedEmailAddressUserInfo) {
    const color = this.getOptionColor(sharedEmailAddressUserInfo);

    return color ? themedFillTextClass(color, "white-font") : "white-font";
  }

  getOptionColor(sharedEmailAddressUserInfo: SharedEmailAddressUserInfo) {
    const activeUser = getActiveUser();

    if (!activeUser) {
      return false;
    }
    
    return activeUser.id === sharedEmailAddressUserInfo.userId ? COLOR_BUTTON_GREEN : COLOR_BUTTON;
  }

  getName(sharedEmailAddressUserInfo: SharedEmailAddressUserInfo) {
    return `${sharedEmailAddressUserInfo.clientName} (${sharedEmailAddressUserInfo.userNickname})`;
  }

  async loginForSharedEmailAddressUserInfo(
    sharedEmailAddressInfo: SharedEmailAddressInfo,
    sharedEmailAddressUserInfo: SharedEmailAddressUserInfo,
    sharedUserPassword: string | null,
  ) {
    let calledViaHeader = false;
    if (this.calledViaHeader) {
      calledViaHeader = true;
    }

    const result = await signInWithUserOfSharedEmailAddress(
      sharedEmailAddressInfo.id,
      sharedEmailAddressUserInfo.userId,
      sharedEmailAddressUserInfo.clientId,
      calledViaHeader,
    );

    if (result.wasSuccessful && result.token) {
      await auth().signInWithCustomToken(result.token);

      const setUserResult = await setActiveUser(
        sharedEmailAddressUserInfo.userId,
        sharedUserPassword
      );

      return setUserResult;
    }

    // #2610066: the chosen user, or their client, is blocked.
    const refusal = toSignInRefusalReason(result.reason);
    if (refusal) {
      SnackbarModule.showError({ key: signInRefusalMessageKey(refusal) });
    }

    return null;
  }

  async onUserInfoSelected(
    sharedEmailAddressUserInfo: SharedEmailAddressUserInfo
  ) {
    const activeUser = getActiveUser();

    if (
      !this.sharedEmailAddressInfo ||
      !activeUser ||
      activeUser.id === sharedEmailAddressUserInfo.userId
    ) {
      return;
    }

    this.isLoading = true;

    if (this.calledViaHeader) {
      clearUserModule();
    }

    const result = await this.loginForSharedEmailAddressUserInfo(
      this.sharedEmailAddressInfo,
      sharedEmailAddressUserInfo,
      this.sharedUserPassword,
    );

    this.isLoading = false;

    if (result != null) {
      this.isShowing = false;
      this.resolve && this.resolve(result);
    }
  }

  async onCancel() {
    await auth().signOut();
    this.isShowing = false;
  }

  async onBack() {
    this.isShowing = false;
  }

  async show(
    sharedEmailAddressInfo: SharedEmailAddressInfo,
    sharedEmailAddressUserOptions: SharedEmailAddressUserInfo[],
    sharedUserPassword: string | null = null,
    calledViaHeader: boolean | null = null,
  ) {
    this.sharedEmailAddressInfo = sharedEmailAddressInfo;
    this.sharedUserPassword = sharedUserPassword;

    if (calledViaHeader) {
      this.calledViaHeader = true;
    }

    this.users = sharedEmailAddressUserOptions.sort((u1, u2) =>
        this.getName(u1).localeCompare(this.getName(u2))
      );

    if (this.users.length === 1 && !this.calledViaHeader) {
      //Automatically select the first user if there is only one option
      const result = await this.loginForSharedEmailAddressUserInfo(
        this.sharedEmailAddressInfo,
        this.users[0],
        sharedUserPassword,
      );

      if (result !== null) {
        this.isShowing = false;
        return result;
      }
    }

    this.isShowing = true;

    return new Promise<LoginResult>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}
